import { z } from "zod";

/**
 * Superadmin capability schemas. The AUTHORITY for refunds, suspensions and
 * bans is an Open Item (never-invent-policy.md) — these endpoints exist as
 * Superadmin-only capability with every use written to audit_log, and are NOT
 * wired to admin/hr/hub_manager until the client defines the policy.
 */
export const setRefundStatusSchema = z.object({
  /** Forward-only record of a refund decision; money movement is out of scope. */
  status: z.enum(["requested", "approved", "paid"]),
  reason: z.string().min(3).max(500),
});

export type SetRefundStatusInput = z.infer<typeof setRefundStatusSchema>;

/**
 * POST /admin/staff — onboard a staff member by INVITATION.
 *
 * ═══ WHY THERE IS NO PASSWORD FIELD, AND MUST NOT BE ═══
 *
 * The admin app used to create staff with a client-side GoTrue `signUp` where
 * an operator typed the new therapist's password. Two problems, both real:
 * the operator then knew a credential that lets them act AS that therapist,
 * and the two writes (auth user, then `staff` row) were not atomic — a failure
 * between them left an orphan auth user that blocked re-using the email.
 *
 * The invite flow fixes both. `service_role` legitimately lives in this Edge
 * Function (never in the bundle — rules/security.md), so GoTrue's admin invite
 * can run server-side and the person sets their own password from the emailed
 * link. Nobody else ever holds it.
 *
 * The AUTHORITY here is NOT an Open Item, unlike the rest of this file: 0003
 * grants `insert on staff` to `hr, admin, superadmin` and `staff_admin_write`
 * scopes it. This endpoint matches that grant rather than inventing one.
 */
export const inviteStaffSchema = z.object({
  fullName: z.string().min(2).max(120),
  email: z.string().email(),
  /**
   * Deliberately excludes `client` (not staff) and `superadmin` (bootstrapped
   * by migration, never through a UI — a superadmin minting a superadmin is a
   * privilege-escalation path with no approval step behind it).
   */
  role: z.enum(["therapist", "rider", "hub_manager", "hr", "admin", "executive"]),
  /**
   * Null = unassigned. Hub-scoped roles are useless without one, but WHICH
   * roles must have a hub is a schema question (`staff.hub_id` is nullable),
   * so this does not invent a requirement the database does not state.
   */
  hubId: z.string().uuid().nullable(),
  /**
   * Superadmin only (enforced in the service): the account is created with
   * this password and NO email is sent — the Super Admin hands it to the rider
   * or therapist in person (Kharie, 2026-10-29). Omitted = the emailed invite
   * above. Never logged, never audited, never returned.
   */
  password: z.string().min(8).max(72).optional(),
});

export type InviteStaffInput = z.infer<typeof inviteStaffSchema>;

export const staffSuspensionSchema = z.object({
  reason: z.string().min(3).max(500),
});

export type StaffSuspensionInput = z.infer<typeof staffSuspensionSchema>;

export const clientBanSchema = z.object({
  reason: z.string().min(3).max(500),
});

export type ClientBanInput = z.infer<typeof clientBanSchema>;

/** GET /admin/audit — keyset cursor over audit_log (created_at desc, id desc). */
export const auditListQuerySchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type AuditListQuery = z.infer<typeof auditListQuerySchema>;

/**
 * RIDER-031/032 — a manual, audited rank change.
 *
 * `reason` is required and not optional-with-a-default: RIDER-032 requires the
 * previous rank, the new rank, the authorized Admin AND the reason, and a
 * defaulted reason records that the field existed rather than why the change
 * was made.
 *
 * ⚠ There is no "recalculate" or "auto" option, deliberately. RIDER-031: do not
 * automatically promote or demote a Rider until the exact measurements and
 * numerical thresholds are separately approved. A rank is set by a person.
 */
export const setRiderRankSchema = z
  .object({
    rank: z.enum(["bronze", "silver", "gold", "diamond"]),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type SetRiderRankInput = z.infer<typeof setRiderRankSchema>;

/**
 * TSESSION-012…015 / TEND-058 — a manual, audited therapist rank change.
 *
 * Identical SHAPE to the rider schema above and deliberately a separate one:
 * `therapist_rank` and `rider_rank` are separate Postgres types (0074:66) for
 * the same reason — they are independent facts about different people, and one
 * shared input type is how a rider route quietly starts accepting a therapist
 * rank.
 *
 * ⚠ THE ROUTE THAT USES THIS IS SUPERADMIN-ONLY, NOT ADMIN. Riders have
 * RIDER-031 naming "an authorized Admin"; therapists have SILENCE — product-spec
 * carries therapist rank only as a frozen INPUT to the securing cycle, never as
 * something with an owner or a mechanism (OQ-80). 0074 therefore granted
 * `set_therapist_rank()` to service_role alone and wrote: "Admin authority over
 * rank is granted for riders BY NAME and for therapists BY NOBODY. Widening the
 * grant later is one line; narrowing it after people have used it is not."
 * That is `never-invent-policy.md` §2 — capability with the authority withheld —
 * and it is why this is not folded into the rider route above.
 */
export const setTherapistRankSchema = z
  .object({
    rank: z.enum(["bronze", "silver", "gold", "diamond"]),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type SetTherapistRankInput = z.infer<typeof setTherapistRankSchema>;

/**
 * RIDER-028 — the emergency guarantee review.
 *
 * ⚠ `amountPhp` is entered by the reviewer, not computed here. The ₱600 figure
 * lives in `packages/logic/rider-earnings.ts` (money-math.md), and the UI shows
 * that number as the suggested value — but a review is a judgement about a
 * Session that did NOT satisfy the guarantee's own conditions, so the amount is
 * the reviewer's and is recorded as theirs.
 *
 * A decline carries an amount of 0; the database enforces that too
 * (`rider_guarantee_declined_is_zero`), because a client that sends a decline
 * with money attached must not be the thing that decides which field wins.
 */
export const reviewRiderGuaranteeSchema = z
  .object({
    decision: z.enum(["approved", "declined"]),
    // ⚠ IGNORED BY THE SERVER since 0220. The approved amount is computed from
    // the settled guarantee basis (`emergencyGuaranteeTopUp`, CORE-07 §4.5);
    // money is never taken from the client. Optional only so an admin build
    // that still sends it is not refused by `.strict()` — remove it once every
    // deployed admin has stopped sending it.
    amountPhp: z.number().min(0).max(10_000).multipleOf(0.01).optional(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ReviewRiderGuaranteeInput = z.infer<typeof reviewRiderGuaranteeSchema>;

/**
 * ═══ Admin Session overrides — OQ-122 ═══
 *
 * Manual Session management for Riders and Therapists. ⚠ CORE-05B §2 and §7.4
 * say in writing that Admin must NOT do this; the client overruled them
 * (DECISIONS.md OQ-122, relayed by Leon 2026-09-04) and the vault amendment has
 * not landed. Migrations 0193/0195 carry the full argument.
 *
 * `reason` is mandatory on every one of these and is not decoration: it is the
 * only field that explains, six weeks later, why a generated Session changed
 * hands. The SQL refuses a blank one too — a client-side minimum is a courtesy,
 * not the control.
 */

export const reassignRiderSessionSchema = z
  .object({
    toRiderId: z.string().uuid(),
    /**
     * REND-005: the maximum extension is chosen BEFORE the secure action, so it
     * belongs to the incoming Rider and is never carried over from whoever held
     * the Session. Same three-value domain as `secureRiderSessionSchema`.
     */
    maxExtensionHours: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ReassignRiderSessionInput = z.infer<typeof reassignRiderSessionSchema>;

/** Rider-session mirror of `retimeTherapistSessionSchema` — see its comment. */
export const retimeRiderSessionSchema = z
  .object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type RetimeRiderSessionInput = z.infer<typeof retimeRiderSessionSchema>;

export const releaseRiderSessionSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ReleaseRiderSessionInput = z.infer<typeof releaseRiderSessionSchema>;

/**
 * ⚠ ONLY THE TWO FINALIZED STATES. `draft_secured` and `draft_off` belong to the
 * Therapist's own pre-Friday securing flow, where `secure_therapist_sessions`
 * enforces the rank minimum, the Off limit and the seven-date shape — none of
 * which the override reproduces. Excluded here as well as in the SQL so a
 * malformed request is a 422 rather than a database refusal.
 */
export const setTherapistSessionStateSchema = z
  .object({
    state: z.enum(["secured_final", "off_final"]),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type SetTherapistSessionStateInput = z.infer<typeof setTherapistSessionStateSchema>;

export const reassignTherapistSessionSchema = z
  .object({
    toTherapistId: z.string().uuid(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ReassignTherapistSessionInput = z.infer<typeof reassignTherapistSessionSchema>;

/**
 * Superadmin-only, stricter than the OQ-122 admin-override family above
 * (`admin`+`superadmin`): CORE-05B §4.9.4 explicitly forbids changing a
 * Session's time, and OQ-122 never covered this verb. Authorized separately
 * (relayed by Leon, 2026-09-22) — restricted to Superadmin because it is new
 * authority, not an extension of the existing one.
 *
 * Time only, never date: the server re-derives the Manila calendar date from
 * `startsAt` and refuses a mismatch against the session's own `service_date`
 * (SERVICE_DATE_MISMATCH) — §4.9.4 still forbids moving a Session to a
 * different day.
 */
export const retimeTherapistSessionSchema = z
  .object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type RetimeTherapistSessionInput = z.infer<typeof retimeTherapistSessionSchema>;

/**
 * End a therapist's Emergency Pause early (0352) — TEND-061 "Support controls
 * continuation". Superadmin only while who Support is stays an Open Item.
 */
export const endTherapistEmergencyPauseSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type EndTherapistEmergencyPauseInput = z.infer<typeof endTherapistEmergencyPauseSchema>;

/**
 * 0248 — Superadmin-only: force a Confirm Pick-Up past the 50m Rider<->
 * Therapist range block (both the initial leg and a Return's). Bypasses ONLY
 * the range check; the pre-existing Cash gate on each still applies. A
 * reason is required, same posture as the session-retime override above.
 */
export const forceConfirmPickUpSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ForceConfirmPickUpInput = z.infer<typeof forceConfirmPickUpSchema>;

/**
 * Replace the Therapist of one Therapist entry of a paid booking — every one of
 * her Clients, one-for-one (THER-040 / SCHED-025, migration 0230). The booking
 * group and the Therapist being replaced come from the URL.
 */
export const replaceTherapistEntrySchema = z
  .object({
    toTherapistId: z.string().uuid(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ReplaceTherapistEntryInput = z.infer<typeof replaceTherapistEntrySchema>;

/**
 * Take a Therapist off SEVERAL finalized dates in one all-or-nothing action —
 * the real case (out sick for the rest of the week), which the per-date endpoint
 * can only do as N separate transactions.
 *
 * ⚠ `remove_therapist_from_cycle` does NOT cover this: its DELETE is scoped to
 * DRAFT rows, so after Friday 7:00 PM it changes membership and leaves all seven
 * finalized dates Secured. 0197 carries the argument.
 *
 * Max 7 because a cycle is one service week and a therapist holds exactly seven
 * date rows in it — a longer array is a caller bug, not a bigger request.
 */
export const offTherapistDaysSchema = z
  .object({
    cycleId: z.string().uuid(),
    therapistId: z.string().uuid(),
    /** `YYYY-MM-DD`, Asia/Manila. Deduped and ordered server-side. */
    dates: z
      .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
      .min(1)
      .max(7),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type OffTherapistDaysInput = z.infer<typeof offTherapistDaysSchema>;

/**
 * ⚠ THE ONE OPERATION CORE-05B FORBIDS BY NAME. §2: "Admin or Hub Manager must
 * not manually create extra supply outside the approved generation rules." It
 * exists only because the client asked for it (OQ-122), and every row it makes
 * is quarantined in its own `emergency_override` batch so it can never be
 * counted as generated supply — 0196 carries the argument.
 *
 * `toRiderId` is REQUIRED, not optional. This creates supply for a named backup
 * who is standing there; an unowned emergency Session would publish anonymous
 * extra supply into the pool, which is the widest possible reading of an
 * authority that already contradicts an approved page. Opening it up afterwards
 * is a second, separately audited act (`/release`).
 */
export const createEmergencyRiderSessionSchema = z
  .object({
    hubId: z.string().uuid(),
    /** `YYYY-MM-DD`, Asia/Manila. The A/B/C window is derived server-side. */
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    code: z.enum(["A", "B", "C"]),
    toRiderId: z.string().uuid(),
    maxExtensionHours: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type CreateEmergencyRiderSessionInput = z.infer<typeof createEmergencyRiderSessionSchema>;

/**
 * Void an emergency Session. Generated Sessions are never voidable (§4.10) —
 * the server refuses with `NOT_EMERGENCY_SUPPLY`, and un-assigning one is
 * `releaseRiderSessionSchema` instead.
 */
export const voidRiderSessionSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type VoidRiderSessionInput = z.infer<typeof voidRiderSessionSchema>;

/**
 * Run a therapist-cycle step by hand (0242). The three steps the cron owns —
 * create, open-priority, open-regular — take no arguments of their own: each
 * derives its week and its eligibility from `now()` inside the SQL.
 *
 * ⚠ There is deliberately no timestamp field. `create_therapist_cycle(p_at)`
 * picks the service week FROM `p_at`, and both openers gate on
 * `<deadline> <= p_at`, so an admin-supplied time would let someone create a
 * cycle for the wrong week or force an opening before its window. The server
 * always passes `now()`.
 *
 * The reason is required only so the `audit_log` row answers why a scheduled
 * job was run by hand.
 */
export const runTherapistCycleStepSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type RunTherapistCycleStepInput = z.infer<typeof runTherapistCycleStepSchema>;

export const therapistCycleListQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(20).default(5),
  })
  .strict();

export type TherapistCycleListQuery = z.infer<typeof therapistCycleListQuerySchema>;

/**
 * Compute a month of tier scores (0244).
 *
 * ⚠ The month is the FIRST of a Manila calendar month — `staff_tier_scores`
 * constrains it, so a mid-month date is refused by the database rather than
 * silently bucketed.
 */
export const computeTierScoresSchema = z
  .object({
    cycleMonth: z.string().regex(/^\d{4}-\d{2}-01$/, "cycleMonth must be the first of a month"),
    /** Which role to score; omitted means both (the monthly cron's shape). */
    role: z.enum(["therapist", "rider", "all"]).optional(),
    /**
     * Apply the scored tiers to rank — the 0329 activation. Guarded
     * server-side: only the most recently completed Manila month may apply,
     * so a historical recompute can never clobber a newer month's ranks.
     */
    apply: z.boolean().optional(),
  })
  .strict();

/**
 * Release a manually-set rank back to the monthly system (0329). Reason
 * required — the release is an audited rank-management decision with the same
 * shape as setting one.
 */
export const releaseRankToSystemSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type ReleaseRankToSystemInput = z.infer<typeof releaseRankToSystemSchema>;

export type ComputeTierScoresInput = z.infer<typeof computeTierScoresSchema>;
